import React, { useState } from 'react';
import {
  Chip, Tooltip, CircularProgress, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  FormControlLabel, Switch, Divider, Box,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  FiberManualRecord as DirtyIcon,
  Save as SaveIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';

/**
 * Toolbar control for a flow's save/sync state, plus a small menu to save on demand and toggle
 * auto-save.
 *
 * Five states, in the order an edit flows through them:
 *  - **Auto-save failed**, the debounced save was rejected or errored. Takes priority over
 *    everything else: the flow is still dirty, and "Unsaved changes" alone would imply a save is
 *    coming when none is. Clickable to retry. Auto-save previously swallowed these into a
 *    `console.warn`, so the safety net could stop working with nothing on screen to say so.
 *  - **Unsaved changes**, local edits not yet persisted. Clickable: clicking saves now (an explicit,
 *    version-snapshotted save) instead of waiting for the debounced auto-save. With auto-save on this
 *    is transient (the debounce window); with it off it stays until the user saves.
 *  - **Syncing…**, persisted locally; syncing to Meta + validating in the background (ADR-0006).
 *  - **Sync failed**, background sync failed; clickable to retry via an immediate save.
 *  - **Synced**, persisted and confirmed by Meta.
 *
 * Validation results are intentionally NOT shown here, Meta's verdict is folded into the single
 * unified Validation indicator, so there's one place to look for issues rather than two that can
 * disagree. An auto-save failure is a different thing: it says the flow is not stored, not that
 * it is invalid.
 */
const SaveStatusControl = ({
  dirty,
  syncStatus,
  autoSaveEnabled,
  onToggleAutoSave,
  onSaveNow,
  canSave,
  autoSaveError,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const sync = syncStatus?.syncStatus;

  const saveNow = () => {
    setAnchorEl(null);
    if (canSave) onSaveNow();
  };

  let chip = null;
  if (autoSaveError) {
    chip = (
      <Tooltip title={`Auto-save failed: ${autoSaveError}. Your changes are still only in this browser. Click to retry.`}>
        <Chip
          size="small"
          color="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          label="Auto-save failed"
          onClick={canSave ? saveNow : undefined}
          sx={{ fontWeight: 500, cursor: canSave ? 'pointer' : 'default' }}
        />
      </Tooltip>
    );
  } else if (dirty) {
    const tip = !canSave
      ? 'Checking for changes on WhatsApp…'
      : autoSaveEnabled
        ? 'Unsaved changes. Saving automatically; click to save now.'
        : 'Unsaved changes. Click to save now.';
    chip = (
      <Tooltip title={tip}>
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          icon={<DirtyIcon sx={{ fontSize: 12 }} />}
          label="Unsaved changes"
          onClick={canSave ? saveNow : undefined}
          sx={{ fontWeight: 500, cursor: canSave ? 'pointer' : 'default' }}
        />
      </Tooltip>
    );
  } else if (sync === 'PENDING' || sync === 'SYNCING') {
    chip = (
      <Tooltip title="Syncing to WhatsApp and validating…">
        <Chip
          size="small"
          variant="outlined"
          icon={<CircularProgress size={14} thickness={5} />}
          label="Syncing…"
          sx={{ fontWeight: 500 }}
        />
      </Tooltip>
    );
  } else if (sync === 'FAILED') {
    chip = (
      <Tooltip title={syncStatus?.syncError || 'Background sync failed. Click to retry.'}>
        <Chip
          size="small"
          color="error"
          variant="outlined"
          icon={<ErrorOutlineIcon />}
          label="Sync failed"
          onClick={canSave ? saveNow : undefined}
          sx={{ cursor: canSave ? 'pointer' : 'default' }}
        />
      </Tooltip>
    );
  } else if (sync === 'SYNCED') {
    chip = (
      <Tooltip title="Synced to Meta">
        <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label="Synced" />
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      {chip}
      <Tooltip title="Save now or toggle auto-save">
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Save options"
          data-testid="save-options-button"
        >
          <ArrowDropDownIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={saveNow} disabled={!canSave}>
          <ListItemIcon><SaveIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Save now</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          disableRipple
          onKeyDown={(e) => e.stopPropagation()}
          sx={{ '&:hover': { backgroundColor: 'transparent' }, cursor: 'default' }}
        >
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                size="small"
                checked={autoSaveEnabled}
                onChange={(e) => onToggleAutoSave(e.target.checked)}
              />
            }
            label="Auto-save"
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SaveStatusControl;
